import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { History, ArrowRight, Loader2 } from "lucide-react";
import { FormValues } from "@/components/FormFields";

const fmt = (f, v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) {
    const sub = f?.sub_fields || [];
    return v.length ? v.map(row => sub.length ? sub.map(sf => `${sf.label}: ${row[sf.id] ?? "—"}`).join(", ") : JSON.stringify(row)).join(" | ") : "—";
  }
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
};

// Revision-by-revision change log for a form submission.
export const FormHistoryDialog = ({ submissionId, open, onOpenChange }) => {
  const [hist, setHist] = useState(null);
  const [showFull, setShowFull] = useState(null);
  useEffect(() => {
    if (!open || !submissionId) return;
    setHist(null);
    axios.get(`${API}/forms/submissions/${submissionId}/history`).then(r => setHist(r.data)).catch(() => setHist({ revisions: [], fields: [] }));
  }, [open, submissionId]);
  const byKey = Object.fromEntries((hist?.fields || []).map(f => [f.id || f.name, f]));
  const revs = [...(hist?.revisions || [])].reverse();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined} data-testid="form-history-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-indigo-600" /> Change history{hist?.form_name ? ` — ${hist.form_name}` : ""}</DialogTitle></DialogHeader>
        {!hist ? <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-600" /></div> : (
          <div className="space-y-3">
            {revs.map(r => (
              <div key={r.revision} className={`border rounded-lg p-3 ${r.current ? "border-indigo-300 bg-indigo-50/40" : ""}`} data-testid={`history-rev-${r.revision}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Badge className={r.current ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-700"}>Rev {r.revision}{r.current ? " · current" : ""}</Badge>
                    <span className="text-xs text-slate-500">{r.edited_by_name || "—"} · {r.edited_at ? new Date(r.edited_at).toLocaleString() : ""}</span>
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setShowFull(showFull === r.revision ? null : r.revision)} data-testid={`history-toggle-${r.revision}`}>{showFull === r.revision ? "Hide full form" : "Show full form"}</Button>
                </div>
                {r.revision === Math.min(...revs.map(x => x.revision)) ? (
                  <p className="text-xs text-slate-500 mt-2">Original submission</p>
                ) : r.changes.length === 0 ? (
                  <p className="text-xs text-slate-500 mt-2">No field changes</p>
                ) : (
                  <div className="mt-2 space-y-1.5">
                    {r.changes.map(c => (
                      <div key={c.key} className="text-sm grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-2" data-testid={`change-${r.revision}-${c.key}`}>
                        <div className="min-w-0"><p className="text-xs text-slate-500">{c.label}</p><p className="line-through text-red-700/80 break-words">{fmt(byKey[c.key], c.old)}</p></div>
                        <ArrowRight className="w-4 h-4 text-slate-400 mt-4" />
                        <div className="min-w-0"><p className="text-xs text-slate-500">&nbsp;</p><p className="font-medium text-green-800 break-words">{fmt(byKey[c.key], c.new)}</p></div>
                      </div>
                    ))}
                  </div>
                )}
                {showFull === r.revision && <div className="mt-3 border-t pt-2"><FormValues fields={hist.fields} data={r.data} /></div>}
              </div>
            ))}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
